/** Card presentation v1. Missing values inherit; null in a patch removes an override. */
export const CARD_APPEARANCE_ENUMS = {
  aspectRatio: ['auto', '9:16', '2:3', '3:4', '1:1', '4:3', '16:9', '21:9'],
  size: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'custom'],
  textMode: ['below', 'overlay', 'hidden', 'beside', 'hover'],
  textAlign: ['left', 'center', 'right'],
  titleSize: ['sm', 'md', 'lg', 'xl'],
  titleWeight: ['normal', 'medium', 'semibold', 'bold'],
  titleFont: ['theme', 'archivo', 'space-grotesk', 'sora'],
  imageFit: ['cover', 'contain'],
  imageSource: ['auto', 'vertical', 'horizontal'],
  imageShape: ['rectangle', 'circle'],
  borderStyle: ['solid', 'dashed', 'dotted'],
  surfaceStyle: ['theme', 'plain', 'glass'],
  shadow: ['none', 'soft', 'medium', 'strong'],
  hoverEffect: ['none', 'lift', 'zoom', 'border'],
  layout: ['grid', 'carousel'],
  rowAlignment: ['left', 'center', 'right'],
} as const;

export const CARD_APPEARANCE_NUMBERS = {
  customWidth: [120, 1920],
  rowPadding: [0, 96],
  titleLines: [1, 4], descriptionLines: [1, 5],
  imagePositionX: [0, 100], imagePositionY: [0, 100], imageZoom: [100, 150],
  radius: [0, 48], borderWidth: [0, 8], overlayOpacity: [0, 90],
  contentPadding: [0, 40], gap: [0, 48],
  columnsDesktop: [0, 6], columnsTablet: [0, 4], columnsMobile: [1, 2],
} as const;
export const CARD_APPEARANCE_BOOLEANS = [
  'showTitle', 'showDescription', 'showProgress', 'showLessonCount', 'showDuration',
  'showBadge', 'showAction', 'showModuleNumber', 'rowDivider', 'rowAutoSize',
] as const;
export const CARD_APPEARANCE_COLORS = [
  'overlayColor', 'backgroundColor', 'textColor', 'borderColor', 'rowBackground',
] as const;

type EnumValues = { [K in keyof typeof CARD_APPEARANCE_ENUMS]: (typeof CARD_APPEARANCE_ENUMS)[K][number] };
type NumberValues = { [K in keyof typeof CARD_APPEARANCE_NUMBERS]: number };
type BooleanValues = { [K in (typeof CARD_APPEARANCE_BOOLEANS)[number]]: boolean };
type ColorValues = { [K in (typeof CARD_APPEARANCE_COLORS)[number]]: string };
export type CardAppearanceValues = EnumValues & NumberValues & BooleanValues & ColorValues;
export type CardAppearance = { [K in keyof CardAppearanceValues]?: CardAppearanceValues[K] | null };
export type CardAppearancePatch = CardAppearance | null;
export type ResolvedCardAppearance = CardAppearanceValues;

/** Defaults can be overridden by each rendering context to preserve its existing UI. */
export const DEFAULT_CARD_APPEARANCE: Readonly<CardAppearanceValues> = Object.freeze({
  aspectRatio: '16:9', size: 'md', customWidth: 960, textMode: 'hidden', textAlign: 'left',
  titleSize: 'md', titleWeight: 'semibold', titleFont: 'theme', titleLines: 2, descriptionLines: 2,
  showTitle: true, showDescription: false, showProgress: true, showLessonCount: true,
  showDuration: true, showBadge: true, showAction: true, showModuleNumber: true,
  imageFit: 'cover', imagePositionX: 50, imagePositionY: 50, imageZoom: 100,
  imageSource: 'auto', imageShape: 'rectangle', radius: 12, borderWidth: 0,
  borderStyle: 'solid', surfaceStyle: 'theme', shadow: 'none', hoverEffect: 'zoom', overlayOpacity: 60,
  overlayColor: '#000000', backgroundColor: '#18181B', textColor: '#FFFFFF',
  borderColor: '#3F3F46', contentPadding: 16, layout: 'grid', gap: 16,
  columnsDesktop: 0, columnsTablet: 0, columnsMobile: 1,
  rowAlignment: 'left', rowPadding: 0, rowDivider: false, rowBackground: '#000000', rowAutoSize: false,
});

const own = (object: object, key: string) => Object.prototype.hasOwnProperty.call(object, key);
const fail = (key: string): never => { throw new TypeError(`Invalid cardAppearance field: ${key}`); };

/** Strict allowlist; no arbitrary CSS, URLs, nested objects or prototype keys. */
export function parseCardAppearance(value: unknown): CardAppearancePatch {
  if (value === null) return null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fail('object');
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return fail('prototype');
  const keys = Reflect.ownKeys(value);
  const maxKeys = Object.keys(CARD_APPEARANCE_ENUMS).length + Object.keys(CARD_APPEARANCE_NUMBERS).length + CARD_APPEARANCE_BOOLEANS.length + CARD_APPEARANCE_COLORS.length;
  if (keys.length > maxKeys) return fail('length');
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    if (typeof key !== 'string') return fail('symbol');
    const enumKey = own(CARD_APPEARANCE_ENUMS, key);
    const numberKey = own(CARD_APPEARANCE_NUMBERS, key);
    const boolKey = (CARD_APPEARANCE_BOOLEANS as readonly string[]).includes(key);
    const colorKey = (CARD_APPEARANCE_COLORS as readonly string[]).includes(key);
    if (!enumKey && !numberKey && !boolKey && !colorKey) return fail(key);
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !('value' in descriptor)) return fail(key);
    const entry: unknown = descriptor.value;
    if (entry !== null) {
      if (enumKey && !(CARD_APPEARANCE_ENUMS[key as keyof EnumValues] as readonly unknown[]).includes(entry)) return fail(key);
      if (numberKey) {
        const [min, max] = CARD_APPEARANCE_NUMBERS[key as keyof NumberValues];
        if (typeof entry !== 'number' || !Number.isFinite(entry) || !Number.isInteger(entry) || entry < min || entry > max) return fail(key);
      }
      if (boolKey && typeof entry !== 'boolean') return fail(key);
      if (colorKey && (typeof entry !== 'string' || !/^#[\da-fA-F]{6}$/.test(entry))) return fail(key);
    }
    result[key] = entry;
  }
  return result as CardAppearance;
}

export function validateCardAppearance(value: unknown): value is CardAppearancePatch {
  try { parseCardAppearance(value); return true; } catch { return false; }
}

/** Patch merge. Undefined preserves, null clears the object, key:null removes that override. */
export function mergeCardAppearance(current: unknown, patch: unknown): CardAppearancePatch {
  if (patch === null) return null;
  const previous = current == null ? null : parseCardAppearance(current);
  if (patch === undefined) return previous;
  const parsed = parseCardAppearance(patch);
  if (parsed === null) return null;
  const result: CardAppearance = { ...previous };
  for (const [key, value] of Object.entries(parsed)) {
    if (value === null) delete result[key as keyof CardAppearance];
    else (result as Record<string, unknown>)[key] = value;
  }
  return result;
}

/** Cascade each level over contextual defaults. Null fields inherit from the earlier level. */
export function resolveCardAppearance(
  contextDefaults: CardAppearance = {},
  ...overrides: (CardAppearancePatch | undefined)[]
): ResolvedCardAppearance {
  const result = { ...DEFAULT_CARD_APPEARANCE };
  for (const layer of [contextDefaults, ...overrides]) {
    if (layer == null) continue;
    const parsed = parseCardAppearance(layer);
    for (const [key, value] of Object.entries(parsed || {})) {
      if (value !== null) (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

/** A module's individual appearance belongs to the course that displays it. */
export type CourseModuleAppearance = {
  defaults: CardAppearancePatch;
  overrides: Record<string, CardAppearance>;
};
export type CourseModuleAppearanceUpdate = {
  defaults?: CardAppearancePatch;
  overrides?: Record<string, CardAppearancePatch>;
  replaceOverrides?: boolean;
};

function courseAppearanceFields(value: unknown, allowed: (key: string) => boolean): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fail('moduleAppearance.object');
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return fail('moduleAppearance.prototype');
  const keys = Reflect.ownKeys(value);
  if (keys.length > 500) return fail('moduleAppearance.length');
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    if (typeof key !== 'string' || !allowed(key)) return fail('moduleAppearance.key');
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !('value' in descriptor)) return fail(`moduleAppearance.${key}`);
    result[key] = descriptor.value;
  }
  return result;
}

/** Strict patch: null removes one scoped override; omitted fields stay untouched. */
export function parseCourseModuleAppearanceUpdate(value: unknown): CourseModuleAppearanceUpdate {
  const fields = courseAppearanceFields(value, (key) => ['defaults', 'overrides', 'replaceOverrides'].includes(key));
  if (!Object.keys(fields).length) return fail('moduleAppearance.empty');
  const result: CourseModuleAppearanceUpdate = {};
  if (own(fields, 'defaults')) result.defaults = parseCardAppearance(fields.defaults);
  if (own(fields, 'replaceOverrides')) {
    if (typeof fields.replaceOverrides !== 'boolean') return fail('moduleAppearance.replaceOverrides');
    result.replaceOverrides = fields.replaceOverrides;
  }
  if (own(fields, 'overrides')) {
    const overrides = courseAppearanceFields(fields.overrides, (key) => /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(key));
    result.overrides = {};
    for (const [moduleId, appearance] of Object.entries(overrides)) {
      result.overrides[moduleId] = parseCardAppearance(appearance);
    }
  }
  return result;
}

/** Stored policies always carry both defaults and the complete scoped override map. */
export function parseCourseModuleAppearance(value: unknown): CourseModuleAppearance {
  const fields = courseAppearanceFields(value, (key) => key === 'defaults' || key === 'overrides');
  if (!own(fields, 'defaults') || !own(fields, 'overrides')) return fail('moduleAppearance.required');
  const parsed = parseCourseModuleAppearanceUpdate(fields);
  const overrides: Record<string, CardAppearance> = {};
  for (const [moduleId, appearance] of Object.entries(parsed.overrides || {})) {
    if (appearance === null) return fail(`moduleAppearance.overrides.${moduleId}`);
    overrides[moduleId] = appearance;
  }
  return { defaults: parsed.defaults ?? null, overrides };
}
